import { useEffect, useRef, useState } from 'react';
import { createWebblePlayer, reportInstanceResolutionFailed, WebbleApiError } from '@webble/wc-player';
import 'video.js/dist/video-js.css';
import { resolveInternalStreamId, StreamLookupError } from '../lib/resolveStreamId';
import ErrorScreen from './ErrorScreen';
import './PlayerView.css';

const ERROR_MESSAGES = {
  network_error: 'Could not reach the streaming instance. Please check your connection and try again.',
  not_found: 'This stream could not be found.',
  unauthorized: 'You are not authorized to view this stream.',
  forbidden: 'You are not allowed to view this stream.',
  malformed_response: 'The streaming instance returned an unexpected response.',
};

function messageFor(err) {
  const code = err instanceof WebbleApiError || err instanceof StreamLookupError ? err.code : null;
  return ERROR_MESSAGES[code] || 'Something went wrong while loading this stream.';
}

function PlayerView({ instanceUrl, streamId, streamIdType }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let player;
    let cancelled = false;
    setError(null);

    async function mount() {
      try {
        const internalStreamId = await resolveInternalStreamId(instanceUrl, streamId, streamIdType);
        if (cancelled) return;

        player = await createWebblePlayer(videoRef.current, {
          instanceUrl,
          streamId: internalStreamId,
        });
      } catch (err) {
        if (cancelled) return;
        // createWebblePlayer reports its own resolution failures; the external-id
        // lookup happens before it, so that one is reported here.
        if (err instanceof StreamLookupError) {
          reportInstanceResolutionFailed(streamId, undefined, err);
        }
        setError(messageFor(err));
      }
    }

    mount();

    return () => {
      cancelled = true;
      player?.dispose();
    };
  }, [instanceUrl, streamId, streamIdType]);

  if (error) {
    return <ErrorScreen title="Playback error" message={error} />;
  }

  return (
    <div className="player-stage">
      <video ref={videoRef} className="video-js webble-player" playsInline />
    </div>
  );
}

export default PlayerView;
